import { InfoOutlined } from '@mui/icons-material'
import { ClickAwayListener, Stack, useMediaQuery } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import React, { MouseEvent, ReactElement, useState } from 'react'
import { Colors } from 'styles/colors'

const HtmlTooltip = styled(({ className, children, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }}>
    {children}
  </Tooltip>
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 240,
    fontSize: theme.typography.pxToRem(20),
    border: `2px solid ${Colors.PINK}`,
  },
  [`& .${tooltipClasses.tooltipArrow} .${tooltipClasses.arrow}`]: {
    color: Colors.PINK,
  },
}))

interface HelpTooltipProps {
  children: ReactElement
  text: string
  disabled?: boolean
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top'
}

export const MobileTooltipWithIcon = ({
  text,
  placement,
}: {
  text: string
  placement?: HelpTooltipProps['placement']
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [open, setOpen] = useState(false)

  const handleTooltipToggle = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen((prev) => !prev)
  }

  const handleTooltipClose = () => {
    setOpen(false)
  }

  const title = (
    <Typography variant="body1" fontWeight={400} color="inherit" onClick={handleTooltipClose}>
      {text}
    </Typography>
  )

  if (!isMobile) {
    return null
  }

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Stack alignItems="center">
        <HtmlTooltip open={open} arrow title={title} placement={placement}>
          <InfoOutlined fontSize="small" onClick={handleTooltipToggle} />
        </HtmlTooltip>
      </Stack>
    </ClickAwayListener>
  )
}

const HelpTooltip = (props: HelpTooltipProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (props.disabled) {
    return props.children
  }

  const title = (
    <Typography variant="body1" fontWeight={400} color="inherit">
      {props.text}
    </Typography>
  )

  // Mobile: show icon with clickable tooltip
  if (isMobile) {
    return props.children
  }

  // Desktop: tooltip on hover
  return (
    <HtmlTooltip arrow title={title} placement={props.placement}>
      {props.children}
    </HtmlTooltip>
  )
}

export default HelpTooltip
